import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken = process.env.REACT_APP_MAP_API!;

interface Props {
  start: [number, number];
  end: [number, number];
  location?: any;
}

const MapBox = ({ start, end, location }: Props) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [driverLocations, setDriverLocations] = useState([
    -98.491484, 29.424534,
  ]);

  const [showTruck, setShowTruck] = useState(true);

  // const [mapzoom, setmapzoom] = useState(14);

  // 🚀 Move these functions OUTSIDE useEffect
  const addRouteLayer = (map: mapboxgl.Map, route: any) => {
    if (map.getLayer("route-line")) map.removeLayer("route-line");
    if (map.getSource("route")) map.removeSource("route");

    map.addSource("route", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: route,
        properties: {},
      },
    });

    map.addLayer({
      id: "route-line",
      type: "line",
      source: "route",
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": "#3b82f6",
        "line-width": 6,
      },
    });

    // Fit map bounds nicely
    map.fitBounds([start, end], {
      padding: 100,
      maxZoom: 14,
      duration: 1000,
    });

    // 🚚 Add start and end markers
    new mapboxgl.Marker().setLngLat(start).addTo(map);
    new mapboxgl.Marker().setLngLat(end).addTo(map);

    // 🛻 Truck Icon
    // map.loadImage(
    //   "https://cdn-icons-png.flaticon.com/512/1995/1995470.png",
    //   (error, image) => {
    //     if (error || !image) return;
    //     if (!map.hasImage("truck")) {
    //       map.addImage("truck", image);
    //     }

    //     if (map.getLayer("truck-layer")) map.removeLayer("truck-layer");
    //     if (map.getSource("truck-marker")) map.removeSource("truck-marker");

    //     map.addSource("truck-marker", {
    //       type: "geojson",
    //       data: {
    //         type: "FeatureCollection",
    //         features: [
    //           {
    //             type: "Feature",
    //             geometry: {
    //               type: "Point",
    //               // coordinates: [77.0379356, 28.4621457],
    //               coordinates: driverLocations,
    //             },
    //             properties: {},
    //           },
    //         ],
    //       },
    //     });

    //     map.addLayer({
    //       id: "truck-layer",
    //       type: "symbol",
    //       source: "truck-marker",
    //       layout: {
    //         "icon-image": "truck",
    //         "icon-size": 0.05,
    //       },
    //     });
    //   },
    // );

    if (showTruck) {
      map.loadImage(
        "https://cdn-icons-png.flaticon.com/512/713/713311.png",
        (error, image) => {
          if (error || !image) return;
          if (!map.hasImage("truck")) {
            map.addImage("truck", image);
          }

          if (map.getLayer("truck-layer")) map.removeLayer("truck-layer");
          if (map.getSource("truck-marker")) map.removeSource("truck-marker");

          map.addSource("truck-marker", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [
                {
                  type: "Feature",
                  geometry: {
                    type: "Point",
                    coordinates: driverLocations,
                  },
                  properties: {},
                },
              ],
            },
          });

          map.addLayer({
            id: "truck-layer",
            type: "symbol",
            source: "truck-marker",
            layout: {
              "icon-image": "truck",
              "icon-size": 0.06,
            },
          });
        },
      );
    } else {
      // 🛑 Hide the truck if showTruck is false
      if (map.getLayer("truck-layer")) map.removeLayer("truck-layer");
      if (map.getSource("truck-marker")) map.removeSource("truck-marker");
    }
  };

  const updateRoute = async () => {
    const map = mapRef.current;
    if (!map) return;

    try {
      const query = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${start.join(",")};${end.join(",")}?geometries=geojson&access_token=${mapboxgl.accessToken}`,
      );
      const data = await query.json();
      const route = data.routes[0].geometry;

      // Distance calculation
      const distanceInMeters = data.routes[0].distance;
      const distanceInKm = (distanceInMeters / 1000).toFixed(2);
      const distanceInMiles = (distanceInMeters / 1609.34).toFixed(2);

      // 🚀 Only call addRouteLayer once
      if (map.isStyleLoaded()) {
        addRouteLayer(map, route);
      } else {
        map.once("load", () => addRouteLayer(map, route));
      }
    } catch (error) {
      console.error("Failed to fetch route", error);
    }
  };

  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [78.9629, 20.5937], // India center
      zoom: 3,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    mapRef.current.on("load", () => {
      setDriverLocations(start);
      updateRoute();
    });

    // 👇 Add zoom listener
    mapRef.current.on("zoom", () => {
      const currentZoom = mapRef.current?.getZoom();

      // You can also set it into state if you want to display
    });

    return () => {
      mapRef.current?.remove();
    };
  }, []);

  useEffect(() => {
    if (mapRef.current && location && start && end) {
      setDriverLocations([location.longitude, location.latitude]);
      setShowTruck(true);
      updateRoute();
    }

    if (mapRef.current && start && end) {
      updateRoute();
    }
  }, [start, end, location]);

  return (
    <div>
      {/* <button
        className="btn bg-blue-500 text-white mb-3 rounded p-3"
        onClick={() => {
          if (mapRef.current && location) {
    
            setDriverLocations([-82.03363, 28.944465]);
            setShowTruck(true);
            updateRoute();

            // // 👇 New: Set zoom dynamically
            // mapRef.current.flyTo({
            //   center: [77.048925, 28.481135], // move to driver location
            //   zoom: 15, // 👈 set the zoom level you want
            //   speed: 1.2, // how fast to fly
            //   curve: 1.42, // smoothness
            //   easing: (t) => t, // default easing
            // });
          }
        }}
      >
        GET DRIVER LOCATION
      </button> */}
      <div
        ref={mapContainerRef}
        style={{ width: "100%", height: "400px", borderRadius: "8px" }}
      />
    </div>
  );
};

export default MapBox;
